import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Stack,
  Button,
  Chip,
  Paper,
} from "@mui/material";
import {
  Email as EmailIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  AutoGraph as AutoGraphIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { ColorModeContext } from "../theme/ThemeContext";

const teamMembers = [
  {
    name: "Tuong Phuc Ngo",
    role: "Researcher",
    institution: "Macquarie University",
    email: ["phuctuong.ngo@students.mq.edu.au", "phuctuong04@gmail.com"],
    avatar: "/team-avatars/tuong-phuc-ngo.jpg",
  },
  {
    name: "Sajjad Nakhwa",
    role: "Researcher",
    institution: "Indian Institute of Technology Bombay",
    email: "23b0702@iitb.ac.in",
    avatar: "/team-avatars/sajjad-nakhwa.jpg",
  },
  // {
  //   name: "Jack Ryan",
  //   role: "Researcher",
  //   institution: "Macquarie University",
  //   email: "jack.ryan2@students.mq.edu.au",
  //   avatar: "/team-avatars/jack-ryan.jpg",
  // },
  {
    name: "Weijun Li",
    role: "Researcher",
    institution: "Macquarie University",
    email: "weijun.li1@students.mq.edu.au",
    avatar: "/team-avatars/weijun-li.png",
  },
  {
    name: "Anudeex Shetty",
    role: "Researcher",
    institution: "Macquarie University & University of Melbourne",
    email: "anudeex@unimelb.edu.au",
    avatar: "/team-avatars/anudeex-shetty.jpg",
  },
  {
    name: "Qiongkai Xu",
    role: "Corresponding Author",
    institution: "Macquarie University & University of Melbourne",
    email: "qiongkai.xu@mq.edu.au",
    avatar: "/team-avatars/qiongkai-xu.jpg",
  },
];
const AboutTeam = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const marqueeRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  // Card width + gap (320px card + 32px gap from theme spacing 4)
  const CARD_WIDTH = 352;

  const handleNav = useCallback(
    (direction) => {
      if (!marqueeRef.current) return;

      // Pause animation
      setIsPaused(true);

      // Calculate current scroll position and shift by one card
      const currentTransform =
        marqueeRef.current.style.transform || "translateX(0)";
      const match = currentTransform.match(/translateX\(([^)]+)\)/);
      const currentX = match ? parseFloat(match[1]) : 0;
      const shiftAmount = direction === "left" ? CARD_WIDTH : -CARD_WIDTH;
      const newX = currentX + shiftAmount;

      marqueeRef.current.style.transform = `translateX(${newX}px)`;

      // Resume animation after a short delay
      setTimeout(() => {
        setIsPaused(false);
      }, 2000);
    },
    [CARD_WIDTH]
  );

  // Duplicate the team members for seamless infinite scrolling
  const scrollItems = [...teamMembers, ...teamMembers];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.paper",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <IconButton
        onClick={useContext(ColorModeContext).toggleColorMode}
        color="inherit"
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 10,
        }}
        aria-label="toggle theme"
      >
        {theme.palette.mode === "dark" ? <Brightness7 /> : <Brightness4 />}
      </IconButton>
      {/* Define Keyframes for Marquee */}
      <style>
        {`
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}
      </style>

      {/* === HERO SECTION === */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "background.paper",
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 14 },
        }}
      >
        {/* Background Decorative Blobs */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.primary.main,
              0.2
            )} 0%, transparent 70%)`,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 50,
            left: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.secondary?.main || theme.palette.primary.light,
              0.15
            )} 0%, transparent 70%)`,
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            {/* Left Side: Content */}
            <Grid item xs={12} md={7}>
              <Stack
                spacing={3}
                alignItems={{ xs: "center", md: "flex-start" }}
                textAlign={{ xs: "center", md: "left" }}
              >
                {/* Badge */}
                <Chip
                  label="Research Project"
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: "bold",
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                />

                {/* Title */}
                <Typography
                  variant="h2"
                  component="h1"
                  fontWeight="800"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    lineHeight: 1.1,
                    color: "text.primary",
                  }}
                >
                  AI
                  <Box component="span" sx={{ color: "primary.main" }}>
                    Security
                  </Box>
                  Pulse
                </Typography>

                {/* Subtitle */}
                <Typography
                  variant="h5"
                  color="text.secondary"
                  fontWeight="500"
                  sx={{ fontSize: { xs: "1.25rem", md: "1.5rem" } }}
                >
                  Automating Security Issue Reports for AI Models
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    maxWidth: 600,
                    lineHeight: 1.8,
                    fontSize: "1.05rem",
                  }}
                >
                  A centralised platform designed to streamline the reporting of
                  vulnerabilities in AI models. Featuring anonymous submissions
                  and a user-friendly interface for responsible disclosure.
                </Typography>

                {/* CTAs */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  pt={1}
                >
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => {
                      // Set guest mode flag and navigate to dashboard
                      localStorage.setItem("guest", "1");
                      window.dispatchEvent(new Event("guest-mode-change"));
                      navigate("/dashboard");
                    }}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: "bold",
                    }}
                  >
                    View Platform
                  </Button>
                </Stack>

                {/* Auth Links */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ pt: 1 }}
                >
                  Already have an account?{" "}
                  <Box
                    component="span"
                    onClick={() => navigate("/login")}
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Login
                  </Box>
                  {" or "}
                  <Box
                    component="span"
                    onClick={() => navigate("/register")}
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Sign Up
                  </Box>
                </Typography>
              </Stack>
            </Grid>

            {/* Right Side: Abstract Visualization (Glassmorphism) */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Back Card (Decoration) */}
                <Paper
                  elevation={0}
                  sx={{
                    position: "absolute",
                    top: 40,
                    right: 20,
                    width: 260,
                    height: 300,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 4,
                    transform: "rotate(6deg)",
                    zIndex: 1,
                  }}
                />

                {/* Front Card (Main Visual) */}
                <Paper
                  elevation={6}
                  sx={{
                    position: "relative",
                    width: 320,
                    p: 4,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.background.paper,
                      0.9
                    )} 0%, ${alpha(
                      theme.palette.background.paper,
                      0.95
                    )} 100%)`,
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    zIndex: 2,
                  }}
                >
                  <Stack spacing={3}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: "primary.main",
                          width: 56,
                          height: 56,
                        }}
                      >
                        <SecurityIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Security First
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Vulnerability Reporting
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "background.default"
                            : "grey.50",
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        mb={1}
                      >
                        <AutoGraphIcon color="action" fontSize="small" />
                        <Typography variant="body2" fontWeight="600">
                          Impact Analysis
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          width: "100%",
                          height: 6,
                          bgcolor: "grey.200",
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: "75%",
                            height: "100%",
                            bgcolor: "primary.main",
                          }}
                        />
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      "Ensuring AI safety through transparent reporting
                      mechanisms."
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Team Marquee Section */}
      <Box
        sx={{
          pb: 8,
          mt: -4,
          position: "relative",
          zIndex: 2,
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          align="center"
          gutterBottom
          sx={{ mb: 6, color: "text.primary" }}
        >
          Meet Our Team
        </Typography>

        {/* Navigation Buttons + Marquee Container */}
        <Box sx={{ position: "relative" }}>
          {/* Left Navigation Button */}
          <IconButton
            onClick={() => handleNav("left")}
            sx={{
              position: "absolute",
              left: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: 3,
              "&:hover": {
                bgcolor: "background.paper",
                boxShadow: 6,
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {/* Right Navigation Button */}
          <IconButton
            onClick={() => handleNav("right")}
            sx={{
              position: "absolute",
              right: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: 3,
              "&:hover": {
                bgcolor: "background.paper",
                boxShadow: 6,
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          {/* Marquee Container */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              overflow: "hidden",
              position: "relative",
              "&:hover .marquee-track": {
                animationPlayState: "paused",
              },
              // Mask for fade effect on edges
              maskImage:
                "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
            }}
          >
            {/* Marquee Track */}
            <Box
              ref={marqueeRef}
              className="marquee-track"
              sx={{
                display: "flex",
                width: "max-content",
                animation: isPaused ? "none" : "scroll 40s linear infinite",
                transition: isPaused ? "transform 0.3s ease-out" : "none",
                // Ensure we have enough width for all items
                gap: 4,
                pl: 4, // Initial padding
              }}
            >
              {scrollItems.map((member, index) => (
                <Card
                  key={`${member.name}-${index}`}
                  elevation={0}
                  sx={{
                    // FIXED UNIFORM SIZING RULES
                    width: 320, // Fixed width
                    height: 420, // Fixed height
                    flexShrink: 0, // Prevent shrinking in flex container

                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 4,
                    borderRadius: 4,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    bgcolor: "background.default",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 30px ${alpha(
                        theme.palette.primary.main,
                        0.15
                      )}`,
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Avatar
                    src={member.avatar}
                    alt={member.name}
                    sx={{
                      width: 90,
                      height: 90,
                      mb: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      fontSize: "2.25rem",
                      fontWeight: "bold",
                    }}
                  >
                    {member.name.charAt(0)}
                  </Avatar>

                  <CardContent
                    sx={{
                      p: 0,
                      width: "100%",
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="text.primary"
                      noWrap // Prevent title wrap
                    >
                      {member.name}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{
                        fontWeight: "700",
                        mb: 2,
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        letterSpacing: "0.5px",
                        minHeight: "1.5em", // Reserve space
                      }}
                    >
                      {member.role}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                      sx={{
                        mb: 2,
                        color: "text.secondary",
                        flexGrow: 1, // Push email to bottom
                        alignItems: "flex-start", // Align text to top of this section
                      }}
                    >
                      <SchoolIcon
                        fontSize="small"
                        sx={{ opacity: 0.7, mt: 0.3 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.3,
                          fontSize: "0.875rem",
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 3, // Limit institution lines
                        }}
                      >
                        {member.institution}
                      </Typography>
                    </Stack>

                    <Box sx={{ mt: "auto" }}>
                      {Array.isArray(member.email) ? (
                        member.email.map((email, i) => (
                          <Tooltip title={email} key={i}>
                            <IconButton
                              href={`mailto:${email}`}
                              color="primary"
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.2
                                  ),
                                },
                                mx: 0.5,
                              }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))
                      ) : (
                        <Tooltip title={member.email}>
                          <IconButton
                            href={`mailto:${member.email}`}
                            color="primary"
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AboutTeam;
